/**
 * 全局交互增强（在 main.js 启动时调用一次 initInteractions）
 * ------------------------------------------------------------
 * 1) 双层自定义光标：内核小圆点 + 外圈光环（带惯性拖尾），
 *    并随上下文切换形态——可点击 / 输入 / 拖拽 / 禁用 各不相同。
 * 2) 全局按钮涟漪：事件委托，无需逐个标注。
 * 3) 卡片磁吸高光：hover 时按指针位置写入 CSS 变量，做光斑跟随。
 * 4) 按钮磁吸：光标靠近按钮时按钮被轻轻"吸"向指针，强化手感。
 *
 * 全部基于事件委托 + rAF，性能可控，不侵入业务组件。
 */

// ---- 1) 双层自定义光标 ----
function initCursor() {
    const ring = document.createElement("div");
    ring.className = "cur-ring";
    const dot = document.createElement("div");
    dot.className = "cur-dot";
    document.body.appendChild(ring);
    document.body.appendChild(dot);
    document.body.classList.add("has-cursor");

    let rx = window.innerWidth / 2;
    let ry = window.innerHeight / 2;
    let tx = rx;
    let ty = ry;

    window.addEventListener(
        "pointermove",
        (e) => {
            tx = e.clientX;
            ty = e.clientY;
            // 内核点几乎零延迟，外环跟随有惯性，形成"拖尾"
            dot.style.transform = `translate(${tx}px, ${ty}px)`;
        },
        { passive: true }
    );

    const loop = () => {
        rx += (tx - rx) * 0.2;
        ry += (ty - ry) * 0.2;
        ring.style.transform = `translate(${rx}px, ${ry}px)`;
        requestAnimationFrame(loop);
    };
    requestAnimationFrame(loop);

    // 根据悬停目标切换光标形态
    document.addEventListener("pointerover", (e) => {
        const t = e.target;
        const clickable = t.closest("button, a, .btn, .ico-btn, .nav-item, .pill, .view-switch button, label, [role='button']");
        const typing = t.closest("input, textarea, select, [contenteditable='true']");
        const disabled = t.closest(":disabled, .is-disabled, [disabled]");

        ring.classList.toggle("on-click", !!clickable && !disabled);
        ring.classList.toggle("on-text", !!typing);
        ring.classList.toggle("on-disabled", !!disabled);
        dot.classList.toggle("on-text", !!typing);
    });

    // 按下/松开时光环收缩回弹，给出"点击力度"反馈
    window.addEventListener("pointerdown", () => ring.classList.add("press"), { passive: true });
    window.addEventListener("pointerup", () => ring.classList.remove("press"), { passive: true });

    // 指针离开窗口时隐藏，回来再显示，避免卡在边缘
    document.addEventListener("pointerleave", () => document.body.classList.add("cursor-hide"));
    document.addEventListener("pointerenter", () => document.body.classList.remove("cursor-hide"));
}

// ---- 2) 全局按钮涟漪 ----
function initButtonRipple() {
    document.addEventListener(
        "pointerdown",
        (e) => {
            const btn = e.target.closest(".btn, .ico-btn, .view-switch button, .nav-item");
            if (!btn) return;

            const cs = getComputedStyle(btn);
            if (cs.position === "static") btn.style.position = "relative";
            btn.style.overflow = "hidden";

            const rect = btn.getBoundingClientRect();
            const size = Math.max(rect.width, rect.height);
            const span = document.createElement("span");
            span.className = "rp-wave";
            span.style.width = span.style.height = size + "px";
            span.style.left = e.clientX - rect.left - size / 2 + "px";
            span.style.top = e.clientY - rect.top - size / 2 + "px";
            btn.appendChild(span);
            span.addEventListener("animationend", () => span.remove());
        },
        { passive: true }
    );
}

// ---- 3) 卡片磁吸高光 ----
function initCardSpotlight() {
    document.addEventListener(
        "pointermove",
        (e) => {
            const card = e.target.closest(".card");
            if (!card) return;
            const rect = card.getBoundingClientRect();
            const mx = ((e.clientX - rect.left) / rect.width) * 100;
            const my = ((e.clientY - rect.top) / rect.height) * 100;
            card.style.setProperty("--mx", mx + "%");
            card.style.setProperty("--my", my + "%");
        },
        { passive: true }
    );
}

// ---- 4) 按钮磁吸：光标靠近时按钮微微被吸过去 ----
function initMagnetic() {
    let current = null;

    document.addEventListener(
        "pointermove",
        (e) => {
            const btn = e.target.closest(".btn, .ico-btn");
            // 离开上一个按钮时复位（用 CSS 变量，避免和 :hover 的 transform 打架）
            if (current && current !== btn) {
                current.style.setProperty("--mvx", "0px");
                current.style.setProperty("--mvy", "0px");
                current = null;
            }
            if (!btn) return;
            current = btn;

            const rect = btn.getBoundingClientRect();
            // 指针相对按钮中心的偏移，乘一个系数得到位移；幅度克制，避免误点
            const dx = e.clientX - (rect.left + rect.width / 2);
            const dy = e.clientY - (rect.top + rect.height / 2);
            btn.style.setProperty("--mvx", dx * 0.18 + "px");
            btn.style.setProperty("--mvy", dy * 0.22 + "px");
        },
        { passive: true }
    );

    document.addEventListener(
        "pointerleave",
        () => {
            if (current) {
                current.style.setProperty("--mvx", "0px");
                current.style.setProperty("--mvy", "0px");
                current = null;
            }
        },
        true
    );
}

export function initInteractions() {
    // 只有真正的精确指针（鼠标）才启用自定义光标与磁吸；触屏跳过
    const finePointer = window.matchMedia("(pointer:fine)").matches;
    if (finePointer) {
        initCursor();
        initCardSpotlight();
        initMagnetic();
    }
    initButtonRipple();
}

export default initInteractions;
